import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { ChevronLeft, LogOut, Check, Flame } from "lucide-react";
import { toast } from "sonner";

const AREA_ICONS = {
  "WARM THE ENGINE": "🔥",
  "LOWER-BODY PREP": "🦵",
  "UPPER-BODY PREP": "💪",
  "ARM ACTIVATION": "⚡",
  "PLYOCARE THROWS": "🎯",
};

export default function PlayerWarmup() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [blueprint, setBlueprint] = useState([]);
  const [checked, setChecked] = useState(new Set());
  const [total, setTotal] = useState(0);
  const [pct, setPct] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [b, m] = await Promise.all([
          api.get("/pre-throwing-routine"),
          api.get("/warmup/me/today"),
        ]);
        setBlueprint(b.data?.warmup_blueprint || []);
        setChecked(new Set(m.data?.checked_blocks || []));
        setTotal(m.data?.total_blocks || 0);
        setPct(m.data?.percent_complete || 0);
      } finally { setLoading(false); }
    })();
  }, []);

  const toggle = async (label) => {
    const next = !checked.has(label);
    // Optimistic update
    const optimistic = new Set(checked);
    if (next) optimistic.add(label); else optimistic.delete(label);
    setChecked(optimistic);
    try {
      const r = await api.post("/warmup/check", { block_label: label, checked: next });
      if (r.data?.ok === false) {
        toast.error("Server rejected that block — refresh the page");
        // Revert
        const rev = new Set(checked);
        setChecked(rev);
        return;
      }
      setPct(r.data.percent_complete);
      setTotal(r.data.total_blocks);
      if (next && r.data.percent_complete === 100) {
        toast.success("Warmup complete · arm is ready");
      }
    } catch (e) {
      toast.error("Couldn't save — try again");
      // Revert
      setChecked(new Set(checked));
    }
  };

  const handleLogout = async () => { await logout(); nav("/", { replace: true }); };

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--ink-1)" }} data-testid="player-warmup-page">
      <header className="sticky top-0 z-30 backdrop-blur-xl border-b border-[var(--ink-border)]" style={{ backgroundColor: "rgba(10,20,36,0.85)" }}>
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo size="sm" variant="lockup" linkTo="/portal" dark />
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/70 hover:text-white hover:bg-white/5">
            <LogOut className="h-4 w-4 mr-1.5" /> Sign out
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 pt-6 space-y-5">
        <Link to="/portal" className="inline-flex items-center text-sm text-white/50 hover:text-white">
          <ChevronLeft className="h-4 w-4" /> Back to today&apos;s log
        </Link>

        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Pre-throwing routine</div>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white mt-1">Today&apos;s warmup</h1>
            <p className="text-sm text-white/50 mt-1">Tap each block as you finish. Your coach sees this live.</p>
          </div>
          <div className="text-right" data-testid="warmup-progress">
            <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40">Progress</div>
            <div className="flex items-baseline gap-1.5 mt-1 justify-end">
              <span className="font-display font-black text-4xl font-mono-num text-white">{pct}</span>
              <span className="text-base text-white/40">%</span>
            </div>
            <div className="text-xs text-white/40 font-mono-num">{checked.size} / {total} blocks</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 w-full rounded-full bg-white/5 overflow-hidden" data-testid="warmup-progress-bar">
          <div
            className="h-full transition-all duration-300"
            style={{ width: `${pct}%`, backgroundColor: pct >= 100 ? "#10B981" : "var(--brand-primary)" }}
          />
        </div>

        {loading ? (
          <div className="rounded-xl ink-card p-6 text-center text-white/40">Loading warmup…</div>
        ) : (
          blueprint.map((area) => (
            <section key={area.area} className="rounded-xl ink-card overflow-hidden" data-testid={`warmup-area-${area.area}`}>
              <div className="px-5 py-3 border-b border-[var(--ink-border)] flex items-center gap-2">
                <span className="text-base">{AREA_ICONS[area.area] || "•"}</span>
                <div className="text-[10px] tracking-[0.3em] font-bold text-white/50 uppercase">{area.area}</div>
              </div>
              <ul className="divide-y divide-[var(--ink-border)]">
                {area.blocks.map((blk) => {
                  const done = checked.has(blk.label);
                  return (
                    <li key={blk.label}>
                      <button
                        type="button"
                        onClick={() => toggle(blk.label)}
                        data-testid={`warmup-block-${blk.label}`}
                        className="w-full px-5 py-3 flex items-center gap-3 text-left hover:bg-white/[0.025] transition"
                      >
                        <span
                          className={`flex-shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center transition ${done ? "border-emerald-400 bg-emerald-500/20" : "border-white/20"}`}
                        >
                          {done && <Check className="h-4 w-4 text-emerald-400" />}
                        </span>
                        <span className={`flex-1 text-sm ${done ? "text-white/45 line-through" : "text-white"}`}>{blk.label}</span>
                        {blk.video && (
                          <a href={blk.video} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40 hover:text-cyan-300">
                            Video
                          </a>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))
        )}

        {pct === 100 && (
          <div className="rounded-xl border border-emerald-900/40 bg-emerald-950/30 p-5 text-center" data-testid="warmup-complete-banner">
            <Flame className="h-8 w-8 mx-auto text-emerald-300" />
            <div className="mt-2 font-display font-black text-2xl text-white">Warmup complete</div>
            <div className="mt-1 text-sm text-emerald-200">Arm is ready. Coach has been notified.</div>
          </div>
        )}
      </div>
    </div>
  );
}
